// apps/patient/src/components/appointments/CancelAppointmentDialog.tsx

import { useState } from 'react';

interface CancelAppointmentDialogProps {
  open: boolean;
  cancelling: boolean;
  error?: string | null;
  onClose: () => void;
  onConfirm: (reason: string) => void;
}

export function CancelAppointmentDialog({ open, cancelling, error, onClose, onConfirm }: CancelAppointmentDialogProps) {
  const [reason, setReason] = useState('');

  if (!open) return null;

  const close = () => {
    if (cancelling) return;
    setReason('');
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-md w-full p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Cancel Appointment
        </h3>
        <p className="text-gray-600 mb-4">
          Are you sure you want to cancel this appointment? This action cannot be undone.
        </p>
        {/* Reason is required for patient cancels (link + portal, 2026-10-22); the server enforces it too. */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Reason for cancellation <span className="text-red-600">*</span>
          </label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            rows={3}
            placeholder="Let the office know why"
            required
            disabled={cancelling}
          />
          <p className="text-xs text-gray-500 mt-1">Required.</p>
        </div>
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}
        <div className="flex gap-3">
          <button
            type="button"
            onClick={close}
            className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            disabled={cancelling}
          >
            Keep appointment
          </button>
          <button
            type="button"
            onClick={() => onConfirm(reason.trim())}
            className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={cancelling || reason.trim() === ''}
          >
            {cancelling ? 'Cancelling...' : 'Cancel appointment'}
          </button>
        </div>
      </div>
    </div>
  );
}
